import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";

import {
  Container,
  Box,
  Typography,
  Tabs,
  Tab,
  Badge,
  Paper,
  Chip,
  Button,
} from "@mui/material";
import {
  AddCircle as AddCircleIcon,
  ManageAccounts as ManageAccountsIcon,
  Group as GroupIcon,
  Casino as CasinoIcon,
  MenuBook as MenuBookIcon,
} from "@mui/icons-material";

import CreateGame from "./sections/CreateGame";
import GameManager from "./sections/GameManager";
import Playerlists from "./sections/PlayerLists/Playerlists";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`host-tabpanel-${index}`}
      aria-labelledby={`host-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `host-tab-${index}`,
    "aria-controls": `host-tabpanel-${index}`,
  };
}

function HostPanel() {
  const requests = useSelector((state: any) => state.requests.receivedRequests);
  const { tab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();

  const tabs = [
    { label: "Create Game", slug: "create" },
    { label: "Manage Games", slug: "manage" },
    { label: "Player Lists", slug: "players" },
  ];

  // Get current tab index from URL
  const getTabIndex = () => {
    if (!tab) return 1; // Default to 'Manage Games' tab
    const index = tabs.findIndex(t => t.slug === tab);
    return index >= 0 ? index : 1;
  };

  const [tabValue, setTabValue] = useState(getTabIndex());

  // Redirect to default tab if no tab is specified
  useEffect(() => {
    if (!tab) {
      navigate("/hostpanel/manage", { replace: true });
    }
  }, [tab, navigate]);

  // Update tab when URL changes
  useEffect(() => {
    setTabValue(getTabIndex());
  }, [tab]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    const selectedTab = tabs[newValue];
    if (selectedTab) {
      navigate(`/hostpanel/${selectedTab.slug}`);
    }
  };

  const numRequests = () => {
    const keys = Object.keys(requests);
    const pending = keys.filter((key) => requests[key].status === "pending");
    return pending.length;
  };

  const setDisplay = (value: string) => {
    const tabSlug = value === "playerlists" ? "players" : value;
    navigate(`/hostpanel/${tabSlug}`);
  };

  return (
    <Box sx={{ py: { xs: 2, md: 4 } }}>
      {/* Header Section */}
      <Box sx={{ mb: 4, textAlign: "center" }}>
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            color: "primary.main",
            fontWeight: 600,
          }}
        >
          <CasinoIcon color="primary" fontSize="inherit" />
          Host Panel
        </Typography>
        <Typography
          variant="body1"
          color="textSecondary"
          sx={{ maxWidth: 600, mx: "auto", mb: 2 }}
        >
          Manage your poker games, players, and settings
        </Typography>
        <Button
          variant="outlined"
          startIcon={<MenuBookIcon />}
          href="/hostguide"
          sx={{ borderRadius: 2 }}
        >
          View Host Guide
        </Button>
      </Box>

      <Container maxWidth="lg">
        <Paper elevation={2} sx={{ borderRadius: 2 }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="host panel tabs"
              variant="fullWidth"
              sx={{
                "& .MuiTab-root": {
                  minHeight: 64,
                  fontWeight: "bold",
                  fontSize: "1rem",
                  textTransform: "none",
                },
              }}
            >
              <Tab
                icon={<AddCircleIcon />}
                label="Create Game"
                {...a11yProps(0)}
                sx={{
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 1,
                }}
              />
              <Tab
                icon={
                  <Badge
                    badgeContent={numRequests()}
                    color="error"
                    sx={{
                      "& .MuiBadge-badge": {
                        fontSize: "0.75rem",
                        minWidth: 20,
                        height: 20,
                      },
                    }}
                  >
                    <ManageAccountsIcon />
                  </Badge>
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    Manage Games
                    {numRequests() > 0 && (
                      <Chip
                        label={`${numRequests()} pending`}
                        size="small"
                        color="error"
                        variant="outlined"
                        sx={{ display: { xs: "none", sm: "flex" } }}
                      />
                    )}
                  </Box>
                }
                {...a11yProps(1)}
                sx={{
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 1,
                }}
              />
              <Tab
                icon={<GroupIcon />}
                label="Player Lists"
                {...a11yProps(2)}
                sx={{
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 1,
                }}
              />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <CreateGame setDisplay={setDisplay} />
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <GameManager />
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            <Playerlists />
          </TabPanel>
        </Paper>
      </Container>
    </Box>
  );
}

export default HostPanel;
